import type { PlatformApiMethods } from '@wix/editor-platform-host-integration-apis';
import type {
  DocumentServicesObject,
  TpaStyle,
} from '@wix/document-services-types';
import type {
  TPAHandler,
  TPAMessage,
  TPAMessageCallback,
} from '../../types/tpaMessage';
import type { SettingsCompIdTracker } from './editorTPAHandlers';
import { fontUtils } from '../../utils';
import { AppRefreshService, TpaSettingsPanelService } from '../../services';
import { SelectedComponentData } from '../utils/handlerUtils';
import { AppData } from '../../../../../types/unifiedComponents';
import {
  mapGlobalColorVariablesToLegacyValues,
  mapGlobalFontVariablesToLegacyValues,
} from '../utils/legacyMappers';
import { ComponentRef } from '@wix/editor-platform-sdk-types';

export function createSettingsHandlers(
  platformApiMethods: PlatformApiMethods,
  documentServicesAPI: DocumentServicesObject,
  appRefreshService: AppRefreshService,
  tpaSettingsPanelService: TpaSettingsPanelService,
  getSelectedComponentAppData: () => Promise<SelectedComponentData>,
  settingsCompIdTracker: SettingsCompIdTracker,
) {
  const settingsOpenModal = (msg: TPAMessage, callback: TPAMessageCallback) => {
    tpaSettingsPanelService.openSettingsModal(msg, callback);
  };

  const getDashboardAppUrl = async (
    _mgs: TPAMessage,
    callBack: TPAMessageCallback,
  ) => {
    const { appData } = await getSelectedComponentAppData();

    if (!appData) {
      throw new Error('appData not found for the selected component');
    }

    const url = await platformApiMethods.editor.info.getDashboardUrl(
      appData,
      '',
      {
        appDefinitionId: appData.appDefinitionId || '',
      },
    );

    if (url) {
      callBack(url);
    } else {
      throw new Error(
        `Failed to get dashboard URL for appDefinitionId: ${appData.appDefinitionId}`,
      );
    }
  };

  const triggerSettingsUpdatedEvent: TPAHandler = async (msg: TPAMessage) => {
    const { selectedComp, origCompData } = await getSelectedComponentAppData();

    if (!selectedComp) {
      throw new Error(
        'Component should be selected for openBillingPage handler',
      );
    }

    if (!origCompData) {
      throw new Error('origCompData not found for the selected component');
    }

    documentServicesAPI.tpaV2.change.trigger.settingsUpdated(
      origCompData.appDefinitionId,
      msg.data?.compId,
      msg.data?.message,
    );
  };

  const refreshLivePreview = (msg: TPAMessage) => {
    const { appDefinitionIds, shouldFetchData } = msg?.data ?? {};
    const appsDefinitionIdsToRefresh = [].concat(appDefinitionIds);

    documentServicesAPI.platform.livePreview.refresh({
      apps: appsDefinitionIdsToRefresh,
      source: 'editorTpaHandler',
      shouldFetchData: !!shouldFetchData,
    });
  };

  // Not exposed in TPA SDK, BizMngr postMessages to it directly
  const refreshAppsOnDashboardClose = async (
    msg: TPAMessage,
    _callback: TPAMessageCallback,
  ) => {
    if (!msg?.data) {
      return;
    }

    const appsDefinitionIdsToRefresh = msg?.data?.appsToRefresh as string[];
    const isMessageDataValid =
      typeof appsDefinitionIdsToRefresh === 'string' ||
      (Array.isArray(appsDefinitionIdsToRefresh) &&
        appsDefinitionIdsToRefresh.every((id) => typeof id === 'string'));

    if (isMessageDataValid) {
      appRefreshService.addAppToRefresh(appsDefinitionIdsToRefresh);
    }
  };

  const migrate = async (msg: TPAMessage, callback: TPAMessageCallback) => {
    const { appDefinitionId, payload } = msg?.data ?? {};
    documentServicesAPI.platform
      .migrate(appDefinitionId, payload)
      .then((result: any) => callback(result))
      .catch((error: any) => callback(error));
  };

  const stylesReady = async (
    msg: TPAMessage,
    _callback: TPAMessageCallback,
  ) => {
    const { version } = msg?.data ?? {};

    if (!version) {
      throw new Error(
        '"version" should be present in the message data for stylesReady handler',
      );
    }

    try {
      await platformApiMethods.editor.onStylesReady?.(version);
    } catch (e) {
      // bad compatibility hack for editor 3
    }
  };

  const getWixUpgradeUrl = async (
    _msg: TPAMessage,
    callback: TPAMessageCallback,
  ) => {
    const { appData } = await getSelectedComponentAppData();

    if (
      appData?.appDefinitionId &&
      documentServicesAPI.tpaV2.app.hasPremiumOffering(appData.appDefinitionId)
    ) {
      const isDraftMode = documentServicesAPI.generalInfo.isDraft();
      const isSaveSiteNeeded =
        appData?.notProvisioned ?? (appData?.demoMode || isDraftMode);

      if (isSaveSiteNeeded) {
        await platformApiMethods.editor.saveInBackground('getWixUpgradeUrl');
      }

      const upgradeUrl = platformApiMethods.editor.info.getAppPremiumUrl?.(
        appData.appDefinitionId,
      );

      callback(upgradeUrl);
    }
  };

  const appIsAlive = async (msg: TPAMessage, callback: TPAMessageCallback) => {
    if (
      msg.senderCompId &&
      !documentServicesAPI.components.get.byId(msg.senderCompId)
    ) {
      settingsCompIdTracker.set(msg.compId);
    }

    const sdkVersion = msg?.data?.version;

    try {
      await platformApiMethods.editor.onStylesReady?.(sdkVersion);
    } catch (e) {
      // bad compatibility hack for editor 3
    }

    const editorPointer =
      platformApiMethods.editor.getEditorPointerFromCompId?.(msg.compId);

    const editorPointerWithCurrentVariants =
      platformApiMethods.editor.stage?.getPointerWithCurrentVariants(
        editorPointer,
      );
    const compRef =
      (editorPointerWithCurrentVariants?.documentPointer as ComponentRef) ??
      editorPointer?.documentPointer ??
      platformApiMethods.editor.getCompRefFromCompId?.(msg.compId);

    const getStyle = async (): Promise<TpaStyle> => {
      const dmStyles = documentServicesAPI.tpa.style.getByComp(compRef);

      if (platformApiMethods.document.tpa.getStyleParams) {
        const style = await platformApiMethods.document.tpa.getStyleParams(
          {} as AppData,
          '',
          {
            compRef,
          },
        );

        const legacyColors = mapGlobalColorVariablesToLegacyValues({
          documentServicesAPI,
          colors: style.colors,
        });

        const legacyFonts = mapGlobalFontVariablesToLegacyValues({
          documentServicesAPI,
          fonts: style.fonts,
        });

        return {
          ...dmStyles,
          style: {
            ...style,
            fonts: legacyFonts,
            colors: legacyColors,
          },
        };
      }

      return dmStyles;
    };

    const style = await getStyle();

    style.siteTextPresets =
      await fontUtils.addFontThemeDisplayNameToTextPresets(
        style.siteTextPresets,
      );

    callback(style);
  };

  const closeWindow = async (
    msg: TPAMessage,
    _callback: TPAMessageCallback,
  ) => {
    settingsCompIdTracker.clear();
    const message = msg?.data?.message ?? {};
    const { appData, selectedComp } = await getSelectedComponentAppData();

    platformApiMethods.editor.closePanelsByTarget(
      appData!,
      selectedComp!,
      message,
    );
  };

  return {
    settingsOpenModal,
    getDashboardAppUrl,
    postMessage: triggerSettingsUpdatedEvent,
    refreshLivePreview,
    refreshAppsOnDashboardClose,
    migrate,
    stylesReady,
    getWixUpgradeUrl,
    appIsAlive,
    closeWindow,
  };
}
